// 83 §5 (AUTH-14) — THE BROWSERS THAT DO NOT ASK FOR THE SECOND STEP.
//
// §5, ruled: "A trusted device must be INDIVIDUALLY REMOVABLE by the person,
// REMOVABLE IN BULK, removable by a firm admin, and LISTED TO THE PERSON WITH
// ITS LAST USE." This card is three of those four.
//
// BRIEF 96a §3b — THE FOURTH, CORRECTED. This comment named
// `AuthFactorsAdminPanel`, which brief 81 §8 superseded and brief 96a deleted;
// it had not been mounted since 81, so the sentence had been false for four
// briefs. The surface is `AuthIncidentPanel` (SettingsView.tsx:4227), and the
// claim was CHECKED rather than moved: `DELETE /api/auth-factors/of/:id` calls
// `trusted.dropAllFor(tx, id)` (authFactors.js:985), so an administrator's
// "Revoke everything and end their sessions" takes that colleague's trusted
// browsers with it.
//
// WHAT AN ADMIN STILL CANNOT DO IS REMOVE ONE. The admin's verb is total by
// construction — 81 §8 ruled that "removing rows one at a time is the procedure
// that misses one" — so §5's "individually removable" holds for the PERSON, on
// this card, and not for the administrator. That is a decision, recorded here
// rather than left to be re-derived from an absence.
//
// ── IT IS A ROW OF THE SECOND-STEP PANEL, NOT A CARD OF ITS OWN ─────────────
//
// 83's glance: "the trusted-browsers block floats above PERSONAL PREFERENCES,
// isolated. It belongs inside the second-step panel with everything else about
// signing in." It did, and it read as a stray. So this is now the LIST that sits
// under that panel's trusted-browsers row, and the row above it carries the
// explanation — which is why this component no longer has a lede of its own.
//
// The distinction it used to justify a separate card still holds and is now
// carried by the row rather than by the layout: a factor is a way of PASSING the
// second step, a trusted browser is permission to SKIP it.
//
// ── WHAT IT DOES NOT SHOW ───────────────────────────────────────────────────
//
// The device identifier itself. The server answers a row id, a coarse name, two
// dates and a `current` flag it resolved itself — sending everybody's device id
// would hand one person a correlation handle on their colleagues' machines, for
// nothing anybody needs. `src/lib/deviceId.ts` also fixes the honest wording:
// what is remembered is A BROWSER, "until you clear its data", never "your
// laptop", and the texts below say so.
import { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import {
  listTrustedDevices,
  removeTrustedDevice,
  removeAllTrustedDevices,
  renameTrustedDevice,
} from "../api";
import type { TrustedDevicesResponse } from "../api";

type Toast = (message: string) => void;

/** The same named shape AuthFactorsCard reads, and for the same ratchet reason. */
type ApiFailure = { message?: string };

export default function TrustedDevicesList({ toast }: { toast?: Toast }) {
  const { t, lang } = useTranslation();
  const [data, setData] = useState<TrustedDevicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [renameId, setRenameId] = useState<number | null>(null);
  const [renameTo, setRenameTo] = useState("");

  // The server's own words, already in this person's language — api.ts runs the
  // `srvErr.<code>` lookup at the seam. This only supplies a sentence for a
  // failure that produced no envelope at all. It takes the SENTENCE and not the
  // key, so every key stays a literal first argument to `t()`.
  const say = (e: unknown, sentence: string) =>
    setErr(String((e as ApiFailure)?.message || "").trim() || sentence);

  async function reload() {
    setErr("");
    try {
      setData(await listTrustedDevices());
    } catch (e) {
      say(e, t("auth.trusted.load_failed", { fallback: "Could not read your trusted browsers." }));
    } finally {
      setLoading(false);
    }
  }

  // Once, on mount, in the shape AuthFactorsCard already uses beside this one:
  // the call is INLINE rather than a reference to `reload`, so the dependency
  // array is honestly empty and no suppression comment is owed. `live` guards a
  // component unmounted before the answer arrives.
  useEffect(() => {
    let live = true;
    listTrustedDevices()
      .then((d) => { if (live) setData(d); })
      .catch((e) => {
        if (live) say(e, t("auth.trusted.load_failed", { fallback: "Could not read your trusted browsers." }));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const when = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(lang || undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

  const rename = async (id: number) => {
    const name = renameTo.trim();
    if (!name) { setRenameId(null); return; }
    setBusy(true);
    setErr("");
    try {
      await renameTrustedDevice(id, name);
      setRenameId(null);
      await reload();
    } catch (e) {
      say(e, t("auth.trusted.rename_failed", { fallback: "Could not rename that browser." }));
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (id: number) => {
    setBusy(true);
    setErr("");
    try {
      await removeTrustedDevice(id);
      toast?.(t("auth.trusted.removed", { fallback: "That browser will ask for the second step again." }));
      setConfirmId(null);
      await reload();
    } catch (e) {
      say(e, t("auth.trusted.remove_failed", { fallback: "Could not remove that browser." }));
    } finally {
      setBusy(false);
    }
  };

  const removeAll = async () => {
    setBusy(true);
    setErr("");
    try {
      await removeAllTrustedDevices();
      toast?.(t("auth.trusted.removed_all", { fallback: "Every browser will ask for the second step again." }));
      setConfirmAll(false);
      await reload();
    } catch (e) {
      say(e, t("auth.trusted.remove_failed", { fallback: "Could not remove that browser." }));
    } finally {
      setBusy(false);
    }
  };

  const devices = data?.devices || [];

  return (
    <>
      {err && (
        <div style={{ fontSize: FS.s11, color: "var(--dg, #e5484d)", margin: "0 0 8px" }}>{err}</div>
      )}

      {loading ? (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("common.loading", { fallback: "Loading…" })}
        </p>
      ) : devices.length === 0 ? (
        <p style={{ fontSize: FS.s11, color: "var(--tm)", lineHeight: 1.5 }}>
          {t("auth.trusted.none", {
            fallback: "No browser is trusted. You are asked for the second step every time you sign in.",
          })}
        </p>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
            {devices.map((d) => (
              <div
                key={d.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: FS.s11_5,
                  padding: "6px 0",
                  borderBottom: "1px solid var(--br)",
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  {renameId === d.id ? (
                    // 83's glance — "renameable later from Settings". One field,
                    // in place, so renaming is not a screen either.
                    <input
                      type="text"
                      value={renameTo}
                      autoFocus
                      maxLength={60}
                      onChange={(e) => setRenameTo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") rename(d.id);
                        if (e.key === "Escape") setRenameId(null);
                      }}
                      style={{
                        padding: "4px 7px",
                        background: "var(--bd)",
                        color: "var(--tp)",
                        border: "1px solid var(--br)",
                        borderRadius: 5,
                        fontSize: FS.s11_5,
                        fontFamily: "inherit",
                        outline: "none",
                        width: "100%",
                      }}
                    />
                  ) : (
                    <>
                      <span style={{ fontWeight: 500 }}>
                        {d.label || t("auth.trusted.unnamed", { fallback: "This browser" })}
                      </span>
                      {d.current && (
                        <span style={{ color: "var(--ac)", marginLeft: 8 }}>
                          {t("auth.trusted.this_one", { fallback: "this browser" })}
                        </span>
                      )}
                      {/* 83's glance — name, when trusted, when last used, and
                          roughly where from. The place is what makes an unwanted
                          browser visible without hunting: "office Mac", last used
                          from another country. It is an INDICATION (81 §3's
                          offline lookup) and the line says so by hedging the
                          word rather than by a footnote nobody reads. */}
                      <div style={{ color: "var(--tm)", fontSize: FS.s11, marginTop: 2 }}>
                        {t("auth.trusted.line", {
                          trusted: when(d.trustedAt),
                          last: when(d.lastUsedAt),
                          fallback: `trusted ${when(d.trustedAt)} · last used ${when(d.lastUsedAt)}`,
                        })}
                        {(d.city || d.country) && (
                          <> · {t("auth.trusted.from_about", {
                            place: [d.city, d.country].filter(Boolean).join(", "),
                            fallback: `about ${[d.city, d.country].filter(Boolean).join(", ")}`,
                          })}</>
                        )}
                      </div>
                    </>
                  )}
                </span>
                {confirmId === d.id ? (
                  <>
                    <button type="button" className="btn btn-danger" disabled={busy} onClick={() => removeOne(d.id)}>
                      {t("common.confirm", { fallback: "Confirm" })}
                    </button>
                    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmId(null)}>
                      {t("common.cancel", { fallback: "Cancel" })}
                    </button>
                  </>
                ) : (
                  <>
                    {renameId !== d.id && (
                      <button type="button" className="btn btn-ghost" disabled={busy}
                        onClick={() => { setRenameId(d.id); setRenameTo(d.label || ""); }}>
                        {t("auth.trusted.rename", { fallback: "Rename" })}
                      </button>
                    )}
                    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmId(d.id)}>
                      {t("auth.trusted.remove", { fallback: "Remove" })}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* §5's "removable in bulk". Its own confirmation, because it is the
              act a person reaches for when a machine is MISSING and it must not
              be one stray click away. */}
          {confirmAll ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
              <span style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                {t("auth.trusted.confirm_all", {
                  fallback: "Every browser will ask for the second step again. Continue?",
                })}
              </span>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={removeAll}>
                {t("common.confirm", { fallback: "Confirm" })}
              </button>
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmAll(false)}>
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
            </div>
          ) : (
            /* 83's glance — far right, and the explanatory line above it is gone:
               the row's own sentence already says what a trusted browser is, and
               a second one here was saying it twice. */
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmAll(true)}>
                {t("auth.trusted.remove_all", { fallback: "Forget every browser" })}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
